// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Team form handling only
// 2. Separation of Concerns - Form UI separated from API calls
// 3. Modular Architecture - Reusable form component
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';

import teamManagementService from '../../services/teamManagementService';

const TeamForm = ({ open, team, onClose, onSave }) => {
  const isEditMode = Boolean(team);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    team_type: 'operational',
    is_active: true
  });
  
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load team data when editing
  useEffect(() => {
    if (team) {
      setFormData({
        name: team.name || '',
        code: team.code || '',
        description: team.description || '',
        team_type: team.team_type || 'operational',
        is_active: team.is_active !== undefined ? team.is_active : true
      });
    } else {
      // Reset form for new team
      setFormData({
        name: '',
        code: '',
        description: '',
        team_type: 'operational',
        is_active: true
      });
    }
    setErrors({});
    setError(null);
  }, [team, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate code from name if creating new team
    if (field === 'name' && !isEditMode) {
      const generatedCode = value
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      setFormData(prev => ({ ...prev, code: generatedCode }));
    }
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Team name must be at least 3 characters';
    }
    
    if (!formData.code.trim()) {
      newErrors.code = 'Team code is required';
    } else if (formData.code.length < 3) {
      newErrors.code = 'Team code must be at least 3 characters';
    } else if (!/^[A-Z0-9_]+$/.test(formData.code)) {
      newErrors.code = 'Team code must contain only uppercase letters, numbers, and underscores';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      const teamData = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        description: formData.description.trim() || null,
        team_type: formData.team_type,
        is_active: formData.is_active
      };
      
      if (isEditMode) {
        await teamManagementService.updateTeam(team.id, teamData);
      } else {
        await teamManagementService.createTeam(teamData);
      }
      
      onSave();
    } catch (err) {
      console.error('Failed to save team:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to save team';
      setError(errorMessage);
      
      // Handle specific validation errors
      if (err.response?.status === 400 && errorMessage.includes('already exists')) {
        if (errorMessage.toLowerCase().includes('code')) {
          setErrors(prev => ({ ...prev, code: 'Team code already exists' }));
        } else if (errorMessage.toLowerCase().includes('name')) {
          setErrors(prev => ({ ...prev, name: 'Team name already exists' }));
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit Team' : 'Create New Team'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {/* Team Name */}
          <TextField
            fullWidth
            label="Team Name *"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name || 'Enter a descriptive team name'}
            margin="normal"
            disabled={saving}
          />
          
          {/* Team Code */}
          <TextField
            fullWidth
            label="Team Code *"
            value={formData.code}
            onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
            error={!!errors.code}
            helperText={errors.code || 'Unique identifier (uppercase, numbers, underscores)'}
            margin="normal"
            disabled={saving || isEditMode}
            InputProps={{
              style: { fontFamily: 'monospace' }
            }}
          />
          
          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            multiline
            rows={3}
            helperText="Optional description of the team's purpose"
            margin="normal"
            disabled={saving}
          />
          
          {/* Team Type */}
          <FormControl fullWidth margin="normal" disabled={saving}>
            <InputLabel>Team Type</InputLabel>
            <Select
              value={formData.team_type}
              label="Team Type"
              onChange={(e) => handleChange('team_type', e.target.value)}
            >
              <MenuItem value="operational">Operational</MenuItem>
              <MenuItem value="project">Project</MenuItem>
              <MenuItem value="department">Department</MenuItem>
              <MenuItem value="cross_functional">Cross-Functional</MenuItem>
            </Select>
          </FormControl>
          
          {/* Active Status */}
          {isEditMode && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    disabled={saving}
                  />
                }
                label={formData.is_active ? 'Active' : 'Inactive'}
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Create Team')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TeamForm;
